/**
 * iframe 埋め込みページ（`/embed/{scenarioId}`）専用の全画面制御。
 *
 * iframe の中で開いたモーダルは、どれだけ z-index を上げても iframe の枠内にしか出ない。
 * script 埋め込みと同じ「ページ全体を覆うモーダル」にするには、モーダルを開いている間だけ
 * iframe 自身（＝この document）を Fullscreen API で画面全体に広げるしかない。
 * 親ページに JavaScript を置かないという前提（iframe 1 行だけの貼り付け）での唯一の手段。
 *
 * 前提:
 *   - 親ページの `<iframe>` に `allowfullscreen` / `allow="fullscreen"` が付いていること
 *     （発行する埋め込みコードに含めている）。無ければ要求は拒否され、モーダルは iframe 内に留まる。
 *   - iPhone の Safari は要素の全画面に非対応。`isFrameFullscreenAvailable()` が false を返し、
 *     モーダルは iframe 内に留まる（STUDIO 等の inline-fallback と同じ見え方）。
 *
 * 呼び出しは全て失敗しても例外を投げない。全画面にできなかった場合は
 * 「iframe 内でモーダルが開く」という従来どおりの状態に自然に倒れる。
 */

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
  webkitFullscreenEnabled?: boolean
}

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
}

const noop = () => {}

/** 現在の全画面要素（WebKit 接頭辞つきも見る） */
export const getFullscreenElement = (): Element | null => {
  if (typeof document === 'undefined') return null
  const doc = document as FullscreenDocument
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

/** この document を全画面にできる環境か */
export const isFrameFullscreenAvailable = (): boolean => {
  if (typeof document === 'undefined') return false
  const doc = document as FullscreenDocument
  const root = document.documentElement as FullscreenElement
  const enabled = doc.fullscreenEnabled ?? doc.webkitFullscreenEnabled ?? false
  return enabled && (typeof root.requestFullscreen === 'function' || typeof root.webkitRequestFullscreen === 'function')
}

/**
 * 「全画面でいたい」という最新の意思。
 *
 * requestFullscreen は非同期なので、要求が通る前にモーダルが閉じられると
 * 「モーダルは無いのに全画面のまま」になり得る。解決した時点でこのフラグを見て、
 * 既に不要なら即座に解除する。
 */
let wantsFullscreen = false

/**
 * document 全体を全画面にする。既に全画面なら何もしない。
 */
export const requestFrameFullscreen = (): void => {
  wantsFullscreen = true
  if (!isFrameFullscreenAvailable()) return
  if (getFullscreenElement()) return

  const root = document.documentElement as FullscreenElement
  try {
    const request = root.requestFullscreen
      ? root.requestFullscreen({ navigationUI: 'hide' })
      : root.webkitRequestFullscreen?.()
    Promise.resolve(request)
      .then(() => {
        if (!wantsFullscreen) exitFrameFullscreen()
      })
      .catch(noop)
  } catch {
    // 許可なし（allowfullscreen 無し）やユーザー操作外からの呼び出し。iframe 内表示に留める。
  }
}

/**
 * 全画面を解除する。全画面でなければ何もしない。
 */
export const exitFrameFullscreen = (): void => {
  wantsFullscreen = false
  if (typeof document === 'undefined') return
  if (!getFullscreenElement()) return

  const doc = document as FullscreenDocument
  try {
    const exit = doc.exitFullscreen ? doc.exitFullscreen() : doc.webkitExitFullscreen?.()
    Promise.resolve(exit).catch(noop)
  } catch {
    // 解除できない状態（既に抜けている等）は無視
  }
}

/**
 * 全画面が「解除された」瞬間だけ通知する購読。
 *
 * ブラウザの Esc や「全画面を終了」UI で抜けた場合、モーダルは iframe の小さな枠に
 * 押し込まれたまま残ってしまう。その状態を作らないよう、購読側はモーダルを閉じる。
 * 全画面に入った通知は流さない（入れなかった場合と区別する必要が無いため）。
 *
 * @returns 購読解除関数
 */
export const subscribeFrameFullscreenExit = (onExit: () => void): (() => void) => {
  if (typeof document === 'undefined') return noop
  const handler = () => {
    if (!getFullscreenElement()) onExit()
  }
  document.addEventListener('fullscreenchange', handler)
  document.addEventListener('webkitfullscreenchange', handler)
  return () => {
    document.removeEventListener('fullscreenchange', handler)
    document.removeEventListener('webkitfullscreenchange', handler)
  }
}
